/* Xiaomi · Benefit Outcome panel (LIVE 2026-06-02)
 *
 * Universal panel for capturing how a single (retailer × benefit × month)
 * benefit was delivered. Used in Ayush row, partner 360, journey page.
 */
import { useEffect, useState, useCallback } from 'react';
import { Camera, Video, Trash2, ImageIcon, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';

const STATUS_META = {
  pending:     { label: 'Pending',     color: '#6B7280', bg: '#F3F4F6' },
  confirmed:   { label: 'Confirmed',   color: '#1E3A8A', bg: '#DBEAFE' },
  delivered:   { label: 'Delivered',   color: '#0F766E', bg: '#CCFBF1' },
  closed:      { label: 'Closed',      color: '#374151', bg: '#E5E7EB' },
  declined:    { label: 'Declined',    color: '#7C2D12', bg: '#FED7AA' },
  no_response: { label: 'No response', color: '#92400E', bg: '#FEF3C7' },
};

export default function BenefitOutcomePanel({
  retailerId, benefitId, monthYm, onChange, compact = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState({
    status: 'pending', option_chosen: '', experience_date: '',
    venue_name: '', venue_city: '',
  });
  const [feedback, setFeedback] = useState('');

  const load = useCallback(async () => {
    if (!retailerId || !benefitId || !monthYm) return;
    setLoading(true);
    try {
      const r = await api.get(`/xiaomi/outcomes/${retailerId}/${benefitId}/${monthYm}`);
      const o = r.data.outcome || {};
      setData(o);
      setDraft({
        status: o.status || 'pending',
        option_chosen: o.option_chosen || '',
        experience_date: o.experience_date || '',
        venue_name: o.venue_name || '',
        venue_city: o.venue_city || '',
      });
      setFeedback(o.feedback_text || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load outcome');
    } finally { setLoading(false); }
  }, [retailerId, benefitId, monthYm]);

  useEffect(() => { load(); }, [load]);

  const saveStatus = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = { ...draft, ...overrides };
      await api.post(
        `/xiaomi/outcomes/${retailerId}/${benefitId}/${monthYm}/status`, payload);
      toast.success('Saved');
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const saveFeedback = async () => {
    if (!feedback.trim()) return;
    setSaving(true);
    try {
      await api.post(
        `/xiaomi/outcomes/${retailerId}/${benefitId}/${monthYm}/feedback`,
        { text: feedback.trim() });
      toast.success('Feedback saved · journey Step 12 + 13 auto-ticked');
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Feedback save failed');
    } finally { setSaving(false); }
  };

  const uploadMedia = async (file, caption = '') => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    form.append('caption', caption);
    setSaving(true);
    try {
      await api.post(
        `/xiaomi/outcomes/${retailerId}/${benefitId}/${monthYm}/media`,
        form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${file.name} uploaded`);
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally { setSaving(false); }
  };

  const deleteMedia = async (publicId, resourceType) => {
    if (!window.confirm(`Delete this ${resourceType}?`)) return;
    setSaving(true);
    try {
      const b64 = btoa(publicId).replace(/\//g, '_').replace(/\+/g, '-').replace(/=+$/, '');
      await api.delete(
        `/xiaomi/outcomes/${retailerId}/${benefitId}/${monthYm}/media/${b64}`);
      toast.success('Removed');
      await load();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    } finally { setSaving(false); }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-soft text-sm py-3"
        data-testid="outcome-loading">
        <Loader2 size={14} className="animate-spin" /> Loading outcome…
      </div>
    );
  }
  const media = data?.media || [];
  const photos = media.filter((m) => m.resource_type === 'image');
  const videos = media.filter((m) => m.resource_type === 'video');
  const statusMeta = STATUS_META[draft.status] || STATUS_META.pending;

  return (
    <div data-testid={`outcome-panel-${retailerId}`} className="space-y-3">
      <div className="rounded-lg border bg-white p-3" style={{ borderColor: '#E5E1DC' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-soft">Status</label>
            <select value={draft.status}
              onChange={(e) => { const v = e.target.value;
                setDraft((d) => ({ ...d, status: v }));
                saveStatus({ status: v }); }}
              disabled={saving}
              className="w-full mt-1 px-2 py-1.5 rounded border text-[12px] font-semibold"
              style={{ borderColor: '#E5E1DC', background: statusMeta.bg, color: statusMeta.color }}
              data-testid="outcome-status-select">
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-soft">Experience date</label>
            <input type="date" value={draft.experience_date}
              onChange={(e) => setDraft((d) => ({ ...d, experience_date: e.target.value }))}
              onBlur={() => saveStatus()}
              className="w-full mt-1 px-2 py-1.5 rounded border text-[12px]"
              style={{ borderColor: '#E5E1DC' }}
              data-testid="outcome-date" />
          </div>
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-wider text-soft">
              Option chosen (which venue / hotel / kit?)
            </label>
            <input type="text" value={draft.option_chosen}
              onChange={(e) => setDraft((d) => ({ ...d, option_chosen: e.target.value }))}
              onBlur={() => saveStatus()}
              placeholder="e.g. ITC Grand Chola Chennai · 2N3D"
              className="w-full mt-1 px-2 py-1.5 rounded border text-[12px]"
              style={{ borderColor: '#E5E1DC' }}
              data-testid="outcome-option" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-soft">Venue name</label>
            <input type="text" value={draft.venue_name}
              onChange={(e) => setDraft((d) => ({ ...d, venue_name: e.target.value }))}
              onBlur={() => saveStatus()}
              placeholder="ITC Grand Chola"
              className="w-full mt-1 px-2 py-1.5 rounded border text-[12px]"
              style={{ borderColor: '#E5E1DC' }}
              data-testid="outcome-venue" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider text-soft">Venue city</label>
            <input type="text" value={draft.venue_city}
              onChange={(e) => setDraft((d) => ({ ...d, venue_city: e.target.value }))}
              onBlur={() => saveStatus()}
              placeholder="Chennai"
              className="w-full mt-1 px-2 py-1.5 rounded border text-[12px]"
              style={{ borderColor: '#E5E1DC' }}
              data-testid="outcome-venue-city" />
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-3" style={{ borderColor: '#E5E1DC' }}>
        <label className="text-[10px] uppercase tracking-wider text-soft">
          Written feedback {data?.feedback_at && (
            <span className="ml-1 text-[10px] normal-case" style={{ color: '#0F766E' }}>
              · saved by {data.feedback_by} {new Date(data.feedback_at).toLocaleDateString('en-IN')}
            </span>
          )}
        </label>
        <textarea value={feedback} rows={compact ? 3 : 4}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What did the partner say? Any quotes, photos shared, requests for next time?"
          className="w-full mt-1 px-2 py-1.5 rounded border text-[12px]"
          style={{ borderColor: '#E5E1DC' }}
          data-testid="outcome-feedback-text" />
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] text-soft">{feedback.length}/5000</span>
          <button onClick={saveFeedback}
            disabled={saving || !feedback.trim() || feedback === (data?.feedback_text || '')}
            className="text-[11px] px-3 py-1 rounded text-white font-semibold disabled:opacity-40"
            style={{ background: '#1B2A4A' }}
            data-testid="outcome-feedback-save">
            Save feedback
          </button>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-3" style={{ borderColor: '#E5E1DC' }}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-wider text-soft">
            Photos &amp; videos ({photos.length} photo{photos.length === 1 ? '' : 's'},
            {' '}{videos.length} video{videos.length === 1 ? '' : 's'})
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-white hover:bg-gray-50 cursor-pointer"
              style={{ borderColor: '#E5E1DC' }}
              data-testid="outcome-upload-photo">
              <Camera size={11} /> Photo
              <input type="file" accept="image/*" capture="environment" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0];
                  e.target.value = ''; if (f) uploadMedia(f); }} />
            </label>
            <label className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded border bg-white hover:bg-gray-50 cursor-pointer"
              style={{ borderColor: '#E5E1DC' }}
              data-testid="outcome-upload-video">
              <Video size={11} /> Video
              <input type="file" accept="video/*" capture="environment" className="hidden"
                onChange={(e) => { const f = e.target.files?.[0];
                  e.target.value = ''; if (f) uploadMedia(f); }} />
            </label>
          </div>
        </div>
        {media.length === 0 ? (
          <div className="text-[11px] italic text-soft text-center py-4"
            data-testid="outcome-media-empty">
            <ImageIcon size={20} className="mx-auto mb-1 opacity-30" />
            No photos or videos yet. Drop one above — stored on Cloudinary, persists forever.
          </div>
        ) : (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
            {media.map((m) => (
              <div key={m.public_id} className="relative group rounded overflow-hidden"
                style={{ border: '1px solid #E5E1DC' }}
                data-testid={`outcome-media-${m.public_id.replace(/[^a-zA-Z0-9]/g, '_')}`}>
                {m.resource_type === 'image' ? (
                  <a href={m.url} target="_blank" rel="noopener noreferrer">
                    <img src={m.url} alt={m.caption || m.filename}
                      className="w-full h-20 object-cover" />
                  </a>
                ) : (
                  <a href={m.url} target="_blank" rel="noopener noreferrer"
                    className="w-full h-20 flex items-center justify-center bg-black text-white text-[10px]">
                    <Video size={20} className="mr-1" /> {m.format?.toUpperCase()}
                  </a>
                )}
                {m.caption && (
                  <div className="absolute bottom-0 left-0 right-0 text-[9px] px-1 py-0.5 bg-black/60 text-white truncate"
                    title={m.caption}>{m.caption}</div>
                )}
                <button onClick={() => deleteMedia(m.public_id, m.resource_type)}
                  disabled={saving}
                  className="absolute top-1 right-1 p-1 rounded bg-white/90 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition"
                  title="Delete">
                  <Trash2 size={10} style={{ color: '#DC2626' }} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
